import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCartsForAdmin, getCartFiltersAndSummary } from '../services/adminCartService';
import CartDetailModal from '../components/CartDetailModal';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { CART_TABLE_ID } from '../lib/appwrite';
import './CartsPage.css';

const formatCurrency = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-TZ', { style: 'decimal' });
};

const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { dateStyle: 'short' });
};

const formatDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
};

const CartsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filterData, setFilterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStore, setFilterStore] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('list');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedCart, setSelectedCart] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [itemsRes, filtersRes] = await Promise.all([
        getCartsForAdmin({
          storeId: filterStore || undefined,
          userId: filterUser || undefined,
        }),
        getCartFiltersAndSummary(),
      ]);
      setItems(itemsRes || []);
      setFilterData(filtersRes);
    } catch (err) {
      const msg = err.message || 'Failed to load carts';
      const isAuthError =
        typeof msg === 'string' &&
        (msg.toLowerCase().includes('not authorized') || msg.toLowerCase().includes('unauthorized'));
      setError(
        isAuthError
          ? `${msg} Add REACT_APP_APPWRITE_API_KEY to .env (see .env.example) to use server-level access.`
          : msg
      );
      setItems([]);
      setFilterData(null);
    } finally {
      setLoading(false);
    }
  }, [filterStore, filterUser]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useAutoRefresh(loadData, { realtimeTables: [CART_TABLE_ID] });

  const filteredItems = items.filter((item) => {
    const matchSearch =
      !search ||
      (item.productName || '').toLowerCase().includes(search.toLowerCase()) ||
      (item.storeName || '').toLowerCase().includes(search.toLowerCase()) ||
      (filterData?.userMap?.[item.userId] || '').toLowerCase().includes(search.toLowerCase());
    return matchSearch;
  });

  const summary = filterData?.cartSummary || [];
  const filteredSummary = (filterStore || filterUser)
    ? (() => {
        const byUser = {};
        items.forEach((i) => {
          const uid = i.userId || 'unknown';
          if (!byUser[uid]) byUser[uid] = { userId: uid, itemCount: 0, total: 0, lastUpdated: null };
          byUser[uid].itemCount += 1;
          byUser[uid].total += parseFloat(i.lineTotal) || 0;
          const dt = i.createdAt;
          if (dt && (!byUser[uid].lastUpdated || new Date(dt) > new Date(byUser[uid].lastUpdated))) {
            byUser[uid].lastUpdated = dt;
          }
        });
        return Object.values(byUser);
      })()
    : summary;

  if (loading && !filterData) {
    return (
      <div className="carts-loading">
        <div className="dashboard-spinner" />
        <p>Loading carts...</p>
      </div>
    );
  }

  return (
    <div className="carts-page">
      <div className="carts-header">
        <div>
          <h1 className="dashboard-title">Carts</h1>
          <p className="dashboard-subtitle">
            View active shopping carts. Filter by store or customer.
          </p>
        </div>
      </div>

      {error && (
        <div className="carts-error" role="alert">
          {error}
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <nav className="carts-tabs">
        <button
          type="button"
          className={`carts-tab ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          Cart Items
        </button>
        <button
          type="button"
          className={`carts-tab ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          By Customer
        </button>
      </nav>

      {activeTab === 'list' && (
        <>
          <div className="carts-toolbar">
            <div className="carts-search-wrap">
              <img src="/icons/Search.svg" alt="" className="carts-search-icon" aria-hidden />
              <input
                type="search"
                placeholder="Search by product, store, customer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="carts-search"
              />
            </div>
            <select
              value={filterStore}
              onChange={(e) => setFilterStore(e.target.value)}
              className="carts-filter"
            >
              <option value="">All stores</option>
              {(filterData?.filters?.stores || []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="carts-filter"
            >
              <option value="">All customers</option>
              {(filterData?.filters?.users || []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
            <button type="button" className="carts-btn-refresh" onClick={loadData}>
              ⟳ Refresh
            </button>
          </div>

          <div className="carts-table-wrap">
            <table className="carts-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Store</th>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Price (TZS)</th>
                  <th>Shipping</th>
                  <th>Line Total</th>
                  <th>Added</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="carts-empty">
                      No cart items found.
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => (
                    <tr
                      key={item.$id}
                      className="carts-row-clickable"
                      onClick={() => setSelectedItem(item)}
                    >
                      <td>{item.userName || item.userId || '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="carts-link"
                          onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/stores/${item.storeId}`); }}
                        >
                          {item.storeName || item.storeId || '—'}
                        </button>
                      </td>
                      <td><strong>{item.productName || '—'}</strong></td>
                      <td>{item.quantity ?? '—'}</td>
                      <td>{item.unit || '—'}</td>
                      <td>{formatCurrency(item.price)}</td>
                      <td>{formatCurrency(item.shippingFee)}</td>
                      <td><strong>{formatCurrency(item.lineTotal)}</strong></td>
                      <td>{formatDateTime(item.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'summary' && (
        <div className="carts-summary">
          <p className="carts-summary-desc">Carts grouped by customer</p>
          <div className="carts-summary-grid">
            {filteredSummary.length === 0 ? (
              <p className="carts-summary-empty">No carts found.</p>
            ) : (
              filteredSummary.map((cart) => (
                <div
                  key={cart.userId}
                  className="carts-summary-card carts-summary-card-clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedCart({
                    userId: cart.userId,
                    userName: filterData?.userMap?.[cart.userId] || cart.userId,
                    items: items.filter((i) => i.userId === cart.userId),
                  })}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedCart({
                    userId: cart.userId,
                    userName: filterData?.userMap?.[cart.userId] || cart.userId,
                    items: items.filter((i) => i.userId === cart.userId),
                  })}
                >
                  <h3 className="carts-summary-user">
                    {filterData?.userMap?.[cart.userId] || cart.userId || 'Unknown'}
                  </h3>
                  <div className="carts-summary-stats">
                    <span className="carts-summary-items">{cart.itemCount} item(s)</span>
                    <span className="carts-summary-total">{formatCurrency(cart.total)} TZS</span>
                  </div>
                  <span className="carts-summary-date">
                    Updated: {formatDateTime(cart.lastUpdated)}
                  </span>
                </div>
              ))
            )}
          </div>
          <button type="button" className="carts-summary-refresh" onClick={loadData}>
            ⟳ Refresh
          </button>
        </div>
      )}

      {selectedItem && (
        <CartDetailModal
          mode="item"
          item={selectedItem}
          formatCurrency={formatCurrency}
          formatDateTime={formatDateTime}
          onClose={() => setSelectedItem(null)}
          onNavigateStore={(storeId) => { setSelectedItem(null); navigate(`/dashboard/stores/${storeId}`); }}
        />
      )}
      {selectedCart && (
        <CartDetailModal
          mode="cart"
          cart={selectedCart}
          formatCurrency={formatCurrency}
          formatDateTime={formatDateTime}
          onClose={() => setSelectedCart(null)}
          onNavigateStore={(storeId) => { setSelectedCart(null); navigate(`/dashboard/stores/${storeId}`); }}
        />
      )}
    </div>
  );
};

export default CartsPage;
